import React from 'react';
import { Palette } from 'lucide-react';
import { getContrastTextColor } from '../utils/colorUtils';

interface ColorPickerProps {
  value: string;
  onChange: (color: string) => void;
}

const COLOR_PRESETS = [
  // Light colors
  { name: 'Yellow', color: '#FACC15' },
  { name: 'Blue', color: '#3B82F6' },
  { name: 'Green', color: '#10B981' },
  { name: 'Red', color: '#EF4444' },
  // Dark colors
  { name: 'Navy', color: '#1E3A8A' },
  { name: 'Plum', color: '#5B21B6' },
  { name: 'Charcoal', color: '#374151' },
];

export const ColorPicker: React.FC<ColorPickerProps> = ({ value, onChange }) => {
  return (
    <div className="bg-gradient-to-br from-gray-50 to-white rounded-t-xl shadow-sm border border-gray-300 px-4 py-3">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        {/* Left Section - Title & Color Picker */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex items-center gap-2.5">
            <div className="p-2 bg-yellow-50 rounded-xl">
              <Palette className="w-6 h-6 text-yellow-600" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-900">Brand Accent Color</h3>
              <p className="text-sm text-gray-500">Applied to all buttons & highlights</p>
            </div>
          </div>

          <div className="flex items-center gap-3 sm:pl-4 sm:border-l border-gray-200">
            <input
              type="color"
              id="accentColor"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              className="w-14 h-14 rounded-lg cursor-pointer border-2 border-gray-300 hover:border-gray-400 transition-colors shadow-sm"
            />
            <div className="flex flex-col gap-2 flex-1 sm:flex-initial">
              <input
                type="text"
                value={value}
                onChange={(e) => {
                  const inputValue = e.target.value;
                  if (inputValue.startsWith('#') && inputValue.length <= 7) {
                    onChange(inputValue);
                  }
                }}
                placeholder="#FACC15"
                className="px-3 py-2 border border-gray-300 rounded-lg text-xs font-mono uppercase w-full sm:w-28 focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all"
              />
              <div
                className="px-3 py-2 rounded-lg text-xs font-bold text-center shadow-sm"
                style={{
                  backgroundColor: value,
                  color: getContrastTextColor(value) === 'white' ? 'white' : 'black',
                }}
              >
                BUTTON PREVIEW
              </div>
            </div>
          </div>
        </div>

        {/* Right Section - Quick Presets */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <span className="text-sm font-medium text-gray-600">Quick Preset:</span>
          <div className="flex gap-2 flex-wrap">
            {COLOR_PRESETS.map((preset) => (
              <button
                key={preset.name}
                type="button"
                onClick={() => onChange(preset.color)}
                className="group relative"
                title={preset.name}
              >
                <div
                  className="w-10 h-10 rounded-lg border-2 border-gray-300 hover:border-gray-500 transition-all hover:scale-105 shadow-sm hover:shadow-md"
                  style={{ backgroundColor: preset.color }}
                />
                <span className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-[10px] font-medium text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap bg-white px-2 py-0.5 rounded shadow-sm">
                  {preset.name}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
